<template>
  <grid-column :size="12">
    <div class="card my-100">
      <grid-row :gap="2" class="p-100 pb-0 mb-100">
        <grid-column :size="6">
          <p class="mb-50 text-center"><strong>{{ status }}</strong></p>
          <b-progress
            class="mb-100"
            :type="progressBarData.type"
            :value="progressBarData.value"
            show-value
          >{{ progressBarData.label }}</b-progress>

          <div v-if="validationBadgesIsActive" class="validation-badge text-uppercase text-center">
            <icon-default icon="warning" size="is-small" color="is-warning" class="text-vertical-bottom"></icon-default> {{ warningCount }}
            <icon-default icon="error" size="is-small" color="is-danger" class="text-vertical-bottom"></icon-default> {{ errorCount }}
          </div>
        </grid-column>

        <grid-column :size="6">
          <form-field-input
            label="Label"
            disabled=""
            :value="port.label || 'Unlabeled'"
          ></form-field-input>

          <form-field-input
            label="Type"
            disabled=""
            :value="(port.type === ENUMS.ImportTypeEnum.Record.value) ? 'Leads with Contact' : 'Contacts Only'"
          ></form-field-input>

          <form-field-input
            label="File"
            disabled=""
            :value="port.file_name"
          ></form-field-input>

          <form-field-input
            label="Created At"
            disabled=""
            :value="createdDate"
          ></form-field-input>
        </grid-column>
      </grid-row>

      <grid-row :gap="12" class="px-100 pb-100">
        <grid-column :size="6" class="flex flex-vertical-end">
          <button-link
            color="is-danger"
            @click="deleteImport"
            :disabled="isLoading || isDeleting || !isDeletable"
            :loading="isDeleting"
          >
            Delete
          </button-link>
        </grid-column>

        <grid-column :size="6" class="flex flex-end">
          <button-default
            v-if="(errorCount > 0 || warningCount > 0) && validationBadgesIsActive"
            @click="$refs.validationErrorModal.open()"
            :disabled="isLoading || isDeleting"
          >
            Show Validation Errors
          </button-default>

          <button-default
            class="ml-50"
            v-if="status === 'Validated'"
            @click="importFile"
            :disabled="isLoading || isDeleting"
            :loading="isLoading"
          >
            Import
          </button-default>

          <button-default
            class="ml-50"
            v-if="status === 'Imported' && port.duplicates.length > 0"
            @click="$refs.contactDuplicateModal.open()"
          >
            View Duplicate {{ port.type === ENUMS.ImportTypeEnum.Record.value ? 'Leads' : 'Contacts' }}
          </button-default>

          <button-default
            class="ml-50"
            v-if="status === 'Imported' && port.has_leads_duplicates"
            @click="openDuplicatesModal"
          >
            View Duplicate Leads Imported
          </button-default>

          <button-outlined
            class="ml-50"
            v-if="status === 'Imported'"
            @click="rollBack"
            :disabled="isLoading || isDeleting || !isRollbackable"
            :loading="isLoading"
          >
            Roll Back
          </button-outlined>
        </grid-column>
      </grid-row>

      <b-loading v-model="loaderIsActive" :is-full-page="false"></b-loading>
    </div>

    <import-validation-modal
      ref="validationErrorModal"
      :port="port"
      :error-count="errorCount"
      :warning-count="warningCount"
    ></import-validation-modal>

    <import-duplicate-modal ref="contactDuplicateModal" :port="port" />

    <modal-container>
      <modal-default
        ref="duplicateModal"
        title="Duplicate Imports"
        is-wide
      >
        <div class="email_modal--body">
          <table-paginated ref="duplicateTable" :columns="duplicatePropertyColumns" :load-data="getDuplicateProperties">
            <template v-slot:actions="slotProps">
              <button-default :disabled="isDeletingDuplicates" :loading="isDeletingDuplicates" @click="deleteDuplicateLead(slotProps.row)">Delete Duplicate</button-default>
            </template>
          </table-paginated>
        </div>
      </modal-default>
    </modal-container>
  </grid-column>
</template>

<script>
import {ENUMS} from "../../../services/enums";
import ImportValidationModal from './ImportValidationModal';
import dates from "../../../services/dates";
import EmailPreview from "../../../components/comms/components/EmailPreview.vue";
import TablePaginated from "../../../components/table/TablePaginated.vue";
import ImportDuplicateModal from "./ImportDuplicateModal.vue";

export default {
  components: {
    ImportDuplicateModal,
    TablePaginated,
    EmailPreview,
    ImportValidationModal
  },
  props: {
    // Import is a reserved keyword
    port: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      isDeleting: false,

      loadingStatuses: [
        'Validating',
        'Importing',
        'Rolling Back',
        'File Uploaded'
      ],
      deletableStatuses: [
        'Validated',
        'Failed Validation',
        'Failed Import',
        'Rolled Back',
        'Failed Roll Back'
      ],

      isDeletingDuplicates: false,
      isLoadingDuplicates: false,
      duplicatePropertyColumns: [
        {
          label: 'Address',
          display(property) {
            return property.full_address;
          }
        },
      ]
    }
  },
  computed: {
    status() {
      const status = this.$utils.newGetEnumLabel(this.port.status, ENUMS.ImportStatusEnum)
      if (this.loadingStatuses.includes(status) && this.isPast1Day) return 'Failed Import'

      return status
    },
    createdDate() {
      return dates.formatAsDateTime(this.port.created_at);
    },
    isDeletable() {
      return this.deletableStatuses.includes(this.status) && !this.isPast7Days;
    },
    isRollbackable() {
      return this.status === 'Imported' && !this.isPast7Days;
    },
    loaderIsActive() {
      return this.loadingStatuses.includes(this.status)
    },
    validationBadgesIsActive() {
      const statuses = ['Validated', 'Failed Validation'];
      return statuses.includes(this.status);
    },
    completionPercent() {
      return parseInt((this.port.number_of_records_processed / this.port.total_record_count) * 100);
    },
    warningCount() {
      const warnings = [
        this.port.status_errors.length,
        this.port.campaign_errors.length,
        this.port.column_header_errors.extra_headers.length,
        this.port.column_header_errors.missing_headers.length,
        this.port.duplicates.length,
      ];

      return warnings.reduce((a, b) => a + b);
    },
    errorCount() {
      const keys = Object.keys(this.port.validation_errors);
      const computedKeys = keys.filter(k => k.substring(k.indexOf('.') + 1) != 'property_address_error_header');

      return (computedKeys.length)
    },
    isPast1Day() {
      return dates.isBefore(this.port.created_at, dates.startOfLastXDays(1))
    },
    isPast7Days() {
      return dates.isBefore(this.port.created_at, dates.startOfLast7Days())
    },
    progressBarData() {
      switch (this.status) {
        case 'Validating':
        case 'Importing':
        case 'Rolling Back':
          return {
            label: `${this.port.number_of_records_processed} / ${this.port.total_record_count}`,
            type: 'is-info',
            value: this.completionPercent
          };
        case 'File Uploaded':
          return { label: 'Initiating', type: 'is-info', value: 0 };
        case 'Validated':
          return {
            label: `${this.port.number_of_records_processed} / ${this.port.total_record_count}`,
            type: 'is-success',
            value: 100
          };
        case 'Failed Validation':
          return { label: this.port.general_errors, type: 'is-danger', value: 100 };
        case 'Imported':
          return {
            label: `${this.port.number_of_records_processed} / ${this.port.total_record_count}`,
            type: 'is-success',
            value: 100
          };
        case 'Failed Import':
          return { label: this.port.general_errors, type: 'is-danger', value: 100 };
        case 'Rolled Back':
          return {
            label: `${this.port.number_of_records_processed} / ${this.port.total_record_count}`,
            type: 'is-success',
            value: 100
          };
        case 'Failed Roll Back':
          return { label: this.port.general_errors, type: 'is-danger', value: 100 };
        default:
          return { label: this.port.general_errors, type: 'is-danger', value: 100 };
      }
    }
  },
  watch: {
    status(val) {
      const getOnArr = ['Validated', 'Failed Validation', 'Failed Import', 'Failed Roll Back'];
      if (getOnArr.includes(val)) this.get();

      if (val === 'Imported' || val === 'Rolled Back') {
        this.isLoading = false;
      }
    }
  },
  methods: {
    async deleteDuplicateLead(duplicate) {
      this.isDeletingDuplicates = true;

      return this.$api.imports.deleteDuplicates(this.port.id, duplicate.record_id).then(res => {
        this.$refs.duplicateTable.reloadData().then(() => {
          this.isDeletingDuplicates = false;
        });

        return res;
      })
    },
    openDuplicatesModal() {
      this.isLoadingDuplicates = true;
      this.$refs.duplicateModal.open();
    },
    getDuplicateProperties() {
        return this.$api.imports.getDuplicates(this.port.id);
    },
    get() {
      this.$api.imports.get(this.port.id).then(res => {
        this.$store.commit('updateImport', res.data);
      });
    },
    importFile() {
      this.isLoading = true;
      this.$api.imports.import(this.port.id);
    },
    rollBack() {
      this.$utils.confirm(
        "Are you sure you want to <strong>roll back</strong> this import? All modifications of imported leads will be <strong>lost</strong>",
        'Roll Back'
      )
        .then(() => {
          this.isLoading = true;
          this.$api.imports.rollback(this.port.id)
            .finally(() => { this.isLoading = false });
        })
    },
    deleteImport() {
      this.$utils.confirm(
        "Are you sure you want to <strong>delete</strong> this import?",
        'Delete Import'
      )
        .then(() => {
          this.isDeleting = true;
          this.$api.imports.delete(this.port.id)
            .finally(() => { this.isDeleting = false });
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.progress-wrapper { overflow: hidden; }
</style>
